/**
 * Role React Query Hooks
 *
 * React Query hooks for company role CRUD operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import type {
  CompanyRole,
  CreateRoleDto,
  UpdateRoleDto,
} from '@lognai/plugin-admin';
import type { AccessObject } from '@lognai/plugin-sdk';
import * as roleApi from '../api/roles';
import { roleKeys, platformRoleKeys } from './query-keys';

/**
 * Get list of roles for a company
 */
export const useRoles = (
  companyId: string,
  options?: Omit<UseQueryOptions<CompanyRole[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: roleKeys.list(companyId),
    queryFn: () => roleApi.listRoles(companyId),
    enabled: !!companyId,
    ...options,
  });
};

/**
 * Get a single role by ID
 */
export const useRole = (
  companyId: string,
  roleId: string,
  options?: Omit<UseQueryOptions<CompanyRole>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: roleKeys.detail(companyId, roleId),
    queryFn: () => roleApi.getRole(companyId, roleId),
    enabled: !!companyId && !!roleId,
    ...options,
  });
};

/**
 * Create a new role
 */
export const useCreateRole = (
  companyId: string,
  options?: Omit<UseMutationOptions<CompanyRole, Error, CreateRoleDto>, 'mutationFn' | 'onSuccess'> & {
    onSuccess?: (data: CompanyRole, variables: CreateRoleDto, context: unknown) => void;
  }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto) => roleApi.createRole(companyId, dto),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists(companyId) });
      // One role store since the S4 unification — keep the platform-scoped
      // views (RoleManagementPage) in sync with company-path mutations.
      queryClient.invalidateQueries({ queryKey: platformRoleKeys.all });
      options?.onSuccess?.(data, variables, context);
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
};

/**
 * Update an existing role
 */
export const useUpdateRole = (
  companyId: string,
  options?: Omit<
    UseMutationOptions<CompanyRole, Error, { roleId: string; dto: UpdateRoleDto }>,
    'mutationFn' | 'onSuccess'
  > & {
    onSuccess?: (
      data: CompanyRole,
      variables: { roleId: string; dto: UpdateRoleDto },
      context: unknown
    ) => void;
  }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ roleId, dto }) => roleApi.updateRole(companyId, roleId, dto),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: roleKeys.detail(companyId, variables.roleId),
      });
      queryClient.invalidateQueries({ queryKey: roleKeys.lists(companyId) });
      // One role store since the S4 unification — keep the platform-scoped
      // views (RoleManagementPage) in sync with company-path mutations.
      queryClient.invalidateQueries({ queryKey: platformRoleKeys.all });
      options?.onSuccess?.(data, variables, context);
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
};

/**
 * Delete a role
 */
export const useDeleteRole = (
  companyId: string,
  options?: Omit<UseMutationOptions<void, Error, string>, 'mutationFn' | 'onSuccess'> & {
    onSuccess?: (data: void, variables: string, context: unknown) => void;
  }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (roleId) => roleApi.deleteRole(companyId, roleId),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists(companyId) });
      // One role store since the S4 unification — keep the platform-scoped
      // views (RoleManagementPage) in sync with company-path mutations.
      queryClient.invalidateQueries({ queryKey: platformRoleKeys.all });
      queryClient.removeQueries({
        queryKey: roleKeys.detail(companyId, variables),
      });
      options?.onSuccess?.(data, variables, context);
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
};

/**
 * Copy a role
 */
export const useCopyRole = (
  companyId: string,
  options?: Omit<
    UseMutationOptions<
      CompanyRole,
      Error,
      { roleId: string; data: { code: string; name: string; description?: string } }
    >,
    'mutationFn' | 'onSuccess'
  > & {
    onSuccess?: (
      data: CompanyRole,
      variables: { roleId: string; data: { code: string; name: string; description?: string } },
      context: unknown
    ) => void;
  }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ roleId, data }) => roleApi.copyRole(companyId, roleId, data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists(companyId) });
      // One role store since the S4 unification — keep the platform-scoped
      // views (RoleManagementPage) in sync with company-path mutations.
      queryClient.invalidateQueries({ queryKey: platformRoleKeys.all });
      options?.onSuccess?.(data, variables, context);
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
};

/**
 * The COMPANY-scoped permissions this company may put on its own roles.
 *
 * Separate from `useAccessObjects`, which reads the platform's registry of
 * every access object under a PLATFORM-scoped permission. A company admin
 * holds neither that permission nor any business with the platform surface,
 * so the company role editor reads this instead.
 */
export const useGrantableAccessObjects = (
  companyId: string,
  options?: Omit<UseQueryOptions<AccessObject[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: roleKeys.grantableAccess(companyId),
    queryFn: () => roleApi.listGrantableAccessObjects(companyId),
    enabled: !!companyId,
    // The catalog moves when a plugin is installed or entitled, not minute to
    // minute; refetching it on every mount of the editor is pure noise.
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/** Domain segment of a permission code: `shipment:dd:read` -> `shipment`. */
const domainOf = (code: string): string => code.split(':')[0] ?? '';

/**
 * Administrative surface — granted deliberately, never suggested.
 *
 * Membership, invitation and partnership codes share the `admin:` domain, so
 * a role holding any one of them would otherwise be prompted to take the lot,
 * deletes included.
 */
const isAdministrative = (code: string): boolean =>
  code.startsWith('admin:') || code.startsWith('platform:') || code.startsWith('company:');

/**
 * Permissions a custom role plausibly wants and does not hold.
 *
 * The gap this closes: a plugin contributes its access objects on load and the
 * six seeded tiers pick them up automatically, but a role somebody authored by
 * hand does not — and nothing told anyone. That is how `shipment:dd:read`
 * shipped in one release and a user hit a 403 for it weeks later with no trail
 * in between.
 *
 * Two deliberate choices, both learned from the data:
 *
 * 1. NO DATE TEST. The obvious rule — "created since this role was last
 *    edited" — reads `updatedAt` as a proxy for "a human reviewed the
 *    permissions", and it is a bad one: any rename or level change bumps it
 *    and silences the prompt forever. On real data that is exactly what had
 *    happened to a "Shipment Manager" role edited after the D&D plugin
 *    shipped — the one role whose users were still being refused.
 *
 * 2. SCOPED TO DOMAINS THE ROLE ALREADY WORKS IN. A role holding
 *    `shipment:shipment:read` has an obvious claim on `shipment:dd:read`; it
 *    has none on `analytics:*`. Without this the suggestion list ran to 50+
 *    unrelated codes, and an "add all" over that is the silent over-grant this
 *    whole change exists to avoid.
 *
 * Seeded tiers return nothing: the server-side reconcile maintains them, so a
 * prompt would either duplicate it or contradict it. A role holding nothing
 * yet also returns nothing — there is no domain to infer an interest from, and
 * guessing at a blank role is not a suggestion, it is an invention.
 */
export const suggestedAccessForRole = (
  role: { accessIds?: string[]; isSystem?: boolean },
  catalog: readonly AccessObject[]
): AccessObject[] => {
  if (role.isSystem === true) return [];
  const held = new Set(role.accessIds ?? []);
  if (held.size === 0) return [];

  const byId = new Map(catalog.map((a) => [a.id, a]));
  const heldDomains = new Set(
    [...held]
      .map((id) => byId.get(id)?.code)
      .filter((code): code is string => code !== undefined)
      .map(domainOf)
  );

  return catalog.filter(
    (a) => !held.has(a.id) && heldDomains.has(domainOf(a.code)) && !isAdministrative(a.code)
  );
};
